import { useEffect } from 'react';
import { driver, DeeplinkActionHandler } from '@leagueplatform/masonry';
import { appDeeplinkActionHandler } from 'utils/geisinger-sso';
import { matchesDeeplink } from 'utils/matches-deeplink';

type DeeplinkHandler = typeof DeeplinkActionHandler;

/**
 * While mounted, intercepts the Masonry `deeplink` action so `deeplinkPath` runs
 * `onMatch` instead of navigating. Everything else, including a Contentful `tel:`
 * link, falls through to {@link appDeeplinkActionHandler}.
 *
 * The driver's action controller is a module singleton, so the override is installed
 * only for the calling screen's lifetime and the app handler is restored on unmount.
 */
export const useMasonryDeeplinkInterceptor = (
  deeplinkPath: string,
  onMatch: () => void,
) => {
  useEffect(() => {
    const interceptingHandler: DeeplinkHandler = (action, resources) => {
      if (matchesDeeplink(action.payload.url, deeplinkPath)) {
        // Swallow it: the app serves no such route.
        action.event?.preventDefault();
        onMatch();
        return undefined;
      }
      return appDeeplinkActionHandler(action, resources);
    };

    driver.actionController.overrideHandler(
      'deeplink',
      'default',
      interceptingHandler,
    );

    return () => {
      driver.actionController.overrideHandler(
        'deeplink',
        'default',
        appDeeplinkActionHandler,
      );
    };
  }, [deeplinkPath, onMatch]);
};
